/*
 * Folder tree helpers for the File ▸ Open / Home / Trash screens. Folders come from the API as a flat list
 * ({ id, name, color, parent_id }); everything here is pure and works on that list so the screens can derive
 * children, breadcrumbs, recursive counts and flattened pickers without extra requests.
 */

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true });
const pid = (f) => f.parent_id || null;

/** Direct children of `parentId` (null = top level), sorted by name. */
export const childrenOf = (folders, parentId = null) => (folders || []).filter((f) => pid(f) === (parentId || null)).sort(byName);

/** Root → … → the folder itself (empty when the id is unknown). Cycle-safe. */
export const ancestorsOf = (folders, id) => {
  const map = new Map((folders || []).map((f) => [f.id, f]));
  const out = [];
  const seen = new Set();
  let cur = map.get(id);
  while (cur && !seen.has(cur.id)) { seen.add(cur.id); out.unshift(cur); cur = pid(cur) ? map.get(pid(cur)) : null; }
  return out;
};

/** "Acme Client / Spring Campaign / Drafts". */
export const pathOf = (folders, id, sep = ' / ') => ancestorsOf(folders, id).map((f) => f.name).join(sep);

/** Ids of every folder below `id` (not including it). */
export const descendantIds = (folders, id) => {
  const out = new Set();
  let frontier = [id];
  while (frontier.length) {
    const next = (folders || []).filter((f) => frontier.includes(pid(f)) && !out.has(f.id) && f.id !== id).map((f) => f.id);
    next.forEach((x) => out.add(x));
    frontier = next;
  }
  return out;
};

/** Depth-first flattening for pickers: [{ folder, depth }], optionally leaving out one folder and its subtree. */
export const flattenTree = (folders, { exclude = null } = {}) => {
  const skip = exclude ? new Set([exclude, ...descendantIds(folders, exclude)]) : new Set();
  const out = [];
  const walk = (parentId, depth) => {
    childrenOf(folders, parentId).forEach((f) => { if (skip.has(f.id)) return; out.push({ folder: f, depth }); walk(f.id, depth + 1); });
  };
  walk(null, 0);
  return out;
};

/** { direct: { [folderId]: n, unfiled: n }, deep: { [folderId]: n } } — deep counts include every sub-folder. */
export const folderCounts = (folders, projects) => {
  const direct = { unfiled: 0 };
  (projects || []).forEach((p) => { if (p.folder_id) direct[p.folder_id] = (direct[p.folder_id] || 0) + 1; else direct.unfiled += 1; });
  const deep = {};
  const memo = new Map();
  const sum = (id) => {
    if (memo.has(id)) return memo.get(id);
    memo.set(id, 0); // guards against cycles
    const n = (direct[id] || 0) + childrenOf(folders, id).reduce((s, c) => s + sum(c.id), 0);
    memo.set(id, n);
    return n;
  };
  (folders || []).forEach((f) => { deep[f.id] = sum(f.id); });
  return { direct, deep };
};

/** Days left before a trashed item is purged (never below 0). */
export const daysLeft = (deletedAt, keepDays = 30) => {
  const t = deletedAt ? new Date(deletedAt).getTime() : NaN;
  if (Number.isNaN(t)) return keepDays;
  return Math.max(0, Math.ceil((t + keepDays * 86400000 - Date.now()) / 86400000));
};
